import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Copy,
  Download,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Building2,
  User,
  CreditCard,
  Shield,
  FileCode
} from 'lucide-react';
import { registrationApi } from '../../services/iso20022Api';
import {
  formatDate,
  copyToClipboard,
  downloadFile
} from '../../utils/helpers';
import XmlViewerModal from '../../components/iso20022/XmlViewerModal';

const RegistrationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [xmlModalOpen, setXmlModalOpen] = useState(false);

  useEffect(() => {
    loadRegistration();
  }, [id]);

  const loadRegistration = async () => {
    try {
      setLoading(true);
      const data = await registrationApi.getRegistrationById(id);
      setRegistration(data);
    } catch (err) {
      console.error('Failed to load registration:', err);
      setError('Registration not found');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (field, value) => {
    const success = await copyToClipboard(value);
    if (success) {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    }
  };

  const handleDownloadXml = () => {
    if (registration?.rawXml) {
      const fileName = `registration-acmt007-${registration.id}.xml`;
      downloadFile(registration.rawXml, fileName, 'application/xml');
    }
  };

  const handleViewXml = () => {
    setXmlModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center space-y-4">
          <RefreshCw className="w-8 h-8 text-primary-600 animate-spin" />
          <p className="text-gray-600">Loading registration details...</p>
        </div>
      </div>
    );
  }

  if (error || !registration) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <XCircle className="w-12 h-12 text-danger-500 mx-auto mb-4" />
          <p className="text-gray-900 font-medium mb-2">Registration Not Found</p>
          <p className="text-gray-600 mb-4">{error}</p>
          <button onClick={() => navigate('/iso20022/registrations')} className="btn-primary">
            Back to Registrations
          </button>
        </div>
      </div>
    );
  }

  const InfoRow = ({ label, value, copyable = false, fieldName = '' }) => (
    <div className="py-3 border-b border-gray-200 last:border-0">
      <div className="flex items-center justify-between">
        <dt className="text-sm font-medium text-gray-500">{label}</dt>
        <dd className="text-sm text-gray-900 font-mono flex items-center space-x-2">
          <span>{value || 'N/A'}</span>
          {copyable && value && (
            <button
              onClick={() => handleCopy(fieldName, value)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              title="Copy to clipboard"
            >
              {copiedField === fieldName ? (
                <CheckCircle className="w-4 h-4 text-success-600" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </button>
          )}
        </dd>
      </div>
    </div>
  );

  const getStatusBadgeClass = (status) => {
    if (!status) return 'badge-info';
    
    const statusUpper = status.toUpperCase();
    
    if (statusUpper === 'APPROVED' || statusUpper === 'COMPLETED') {
      return 'badge-success';
    }
    
    if (statusUpper === 'PENDING_APPROVAL' || statusUpper === 'PROCESSING' || statusUpper === 'RECEIVED') {
      return 'badge-warning';
    }
    
    if (statusUpper === 'REJECTED') {
      return 'badge-danger';
    }
    
    return 'badge-info';
  };

  // Determine if XML content is available
  const hasXmlContent = Boolean(registration?.rawXml);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/iso20022/registrations')}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Registration #{registration.id}
            </h1>
            <p className="text-gray-600 mt-1">
              Created {formatDate(registration.createdAt, 'PPpp')}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {hasXmlContent && (
            <>
              <button
                onClick={handleViewXml}
                className="btn-secondary flex items-center space-x-2"
                title="View XML in modal"
              >
                <FileCode className="w-4 h-4" />
                <span>View XML</span>
              </button>
              <button
                onClick={handleDownloadXml}
                className="btn-secondary flex items-center space-x-2"
                title="Download XML file"
              >
                <Download className="w-4 h-4" />
                <span>Download XML</span>
              </button>
            </>
          )}
          <button
            onClick={loadRegistration}
            className="btn-primary flex items-center space-x-2"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Status Banner */}
      <div className={`rounded-lg p-6 ${
        registration.statusCategory === 'success'
          ? 'bg-success-50 border border-success-200'
          : registration.statusCategory === 'rejected'
          ? 'bg-danger-50 border border-danger-200'
          : 'bg-warning-50 border border-warning-200'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {registration.statusCategory === 'success' ? (
              <CheckCircle className="w-8 h-8 text-success-600" />
            ) : registration.statusCategory === 'rejected' ? (
              <XCircle className="w-8 h-8 text-danger-600" />
            ) : (
              <Clock className="w-8 h-8 text-warning-600" />
            )}
            <div>
              <p className="text-sm font-medium text-gray-600">Current Status</p>
              <p className="text-2xl font-bold text-gray-900">
                {registration.statusLabel || registration.status}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm font-medium text-gray-600">Direction</p>
            <span className="badge text-lg bg-purple-100 text-purple-800">
              {registration.directionLabel || registration.direction}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Account Information */}
        <div className="card">
          <div className="card-header flex items-center space-x-2">
            <CreditCard className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Account Information</h2>
          </div>
          <div className="card-body">
            <dl>
              <InfoRow
                label="Account ID"
                value={registration.accountId}
                copyable
                fieldName="accountId"
              />
              <InfoRow
                label="Account Name"
                value={registration.accountName}
              />
              <InfoRow
                label="Account Status"
                value={registration.accountStatusLabel || registration.accountStatus}
              />
              <InfoRow
                label="Currency"
                value={registration.accountCurrency}
              />
              <InfoRow
                label="Account Servicer ID"
                value={registration.accountServicerId}
                copyable
                fieldName="accountServicerId"
              />
            </dl>
          </div>
        </div>

        {/* Organization Information */}
        <div className="card">
          <div className="card-header flex items-center space-x-2">
            <Building2 className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Organization Information</h2>
          </div>
          <div className="card-body">
            <dl>
              <InfoRow
                label="Legal Name"
                value={registration.orgLegalName}
              />
              <InfoRow
                label="Country"
                value={registration.orgCountry}
              />
              <InfoRow
                label="Registration Date"
                value={registration.orgRegistrationDate ? formatDate(registration.orgRegistrationDate, 'PP') : 'N/A'}
              />
              <InfoRow
                label="Merchant Code"
                value={registration.merchantCode}
                copyable
                fieldName="merchantCode"
              />
              <InfoRow
                label="Category"
                value={registration.merchantCategory}
              />
            </dl>
          </div>
        </div>

        {/* Contact Information */}
        <div className="card">
          <div className="card-header flex items-center space-x-2">
            <User className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Contact Information</h2>
          </div>
          <div className="card-body">
            <dl>
              <InfoRow
                label="Sender Name"
                value={registration.senderName}
              />
              <InfoRow
                label="Mobile Number"
                value={registration.senderMobile}
                copyable
                fieldName="senderMobile"
              />
              <InfoRow
                label="Email Address"
                value={registration.senderEmail}
                copyable
                fieldName="senderEmail"
              />
              <InfoRow
                label="Channel Type"
                value={registration.channelType}
              />
            </dl>
          </div>
        </div>

        {/* Mandate Information */}
        <div className="card">
          <div className="card-header flex items-center space-x-2">
            <Shield className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Mandate & Limits</h2>
          </div>
          <div className="card-body">
            <dl>
              <InfoRow
                label="Mandate ID"
                value={registration.mandateId}
                copyable
                fieldName="mandateId"
              />
              <InfoRow
                label="Mandate Channel"
                value={registration.mandateChannel}
              />
              <InfoRow
                label="Maximum Amount"
                value={registration.formattedMandateLimit || 
                       (registration.mandateMaxAmount && registration.mandateCurrency 
                         ? `${registration.mandateMaxAmount} ${registration.mandateCurrency}` 
                         : 'N/A')}
              />
              <InfoRow
                label="Bank Operation Domain"
                value={registration.bankOperationDomain}
              />
            </dl>
          </div>
        </div>

        {/* Technical Details */}
        <div className="card lg:col-span-2">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">Technical Details</h2>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-1">Message ID</dt>
                <dd className="text-sm text-gray-900 font-mono break-all">{registration.msgId}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-1">Process ID</dt>
                <dd className="text-sm text-gray-900 font-mono break-all">{registration.procId || 'N/A'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-1">Signature Valid</dt>
                <dd className="text-sm text-gray-900">{registration.signatureOk ? 'Yes' : 'No'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-1">Created At</dt>
                <dd className="text-sm text-gray-900">{formatDate(registration.createdAt, 'PPpp')}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-1">Updated At</dt>
                <dd className="text-sm text-gray-900">{formatDate(registration.updatedAt, 'PPpp')}</dd>
              </div>
              {registration.attachedDocName && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">Attached Document</dt>
                  <dd className="text-sm text-gray-900">{registration.attachedDocName}</dd>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Organization Address */}
        {registration.orgAddress && (
          <div className="card lg:col-span-2">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-gray-900">Organization Address</h2>
            </div>
            <div className="card-body">
              <p className="text-sm text-gray-900 whitespace-pre-wrap">{registration.orgAddress}</p>
            </div>
          </div>
        )}
      </div>

      {/* XML Content Preview Card - Optional */}
      {hasXmlContent && (
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">XML Preview</h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleViewXml}
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center space-x-1"
              >
                <FileCode className="w-4 h-4" />
                <span>View Full XML</span>
              </button>
              <button
                onClick={() => handleCopy('xml', registration.rawXml)}
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center space-x-1"
              >
                {copiedField === 'xml' ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    <span>Copy XML</span>
                  </>
                )}
              </button>
            </div>
          </div>
          <div className="card-body">
            <pre className="bg-gray-50 p-4 rounded-lg overflow-x-auto text-xs font-mono max-h-64">
              {registration.rawXml.split('\n').slice(0, 20).join('\n')}
              {registration.rawXml.split('\n').length > 20 && '\n...\n(Click "View Full XML" to see complete content)'}
            </pre>
          </div>
        </div>
      )}

      {/* XML Viewer Modal */}
      <XmlViewerModal
        isOpen={xmlModalOpen}
        onClose={() => setXmlModalOpen(false)}
        xmlContent={registration?.rawXml || ''}
        fileName={`registration-acmt007-${registration.id}.xml`}
        title={`Account Registration #${registration.id} - acmt.007`}
      />
    </div>
  );
};

export default RegistrationDetail;
